/**
 * Leave Request List Page — with approval workflow.
 *
 * Shows pending requests as ApprovalCards (manager view) and
 * all requests in a table (employee view). Approve/reject via BLL.
 */

import * as React from 'react';
import { useList } from '@refinedev/core';
import {
  ApprovalCard,
  ListPage,
  EmptyState,
  Button,
  KpiCard,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@yesbos/ui';
import { Umbrella } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Direct BLL call helper ────────────────────────────────────────────────────

async function callBLL(path: string, method = 'POST', body?: unknown): Promise<{ success: boolean; data?: unknown; error?: { message: string } }> {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────────────────

interface LeaveRequest {
  id: string;
  employee_id: string;
  employee_name?: string;
  department?: string;
  leave_type_id: string;
  leave_type_name?: string;
  start_date: string;
  end_date: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  created_at: string;
}

// ── Sub-component: Pending Approvals Panel ────────────────────────────────────

interface PendingPanelProps {
  requests: LeaveRequest[];
  isLoading: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onView: (id: string) => void;
}

function PendingPanel({ requests, isLoading, onApprove, onReject, onView }: PendingPanelProps) {
  if (isLoading) {
    return <div className="text-sm text-muted-foreground p-4">Loading pending requests…</div>;
  }
  if (requests.length === 0) {
    return (
      <EmptyState
        title="No Pending Requests"
        description="All leave requests have been actioned."
      />
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {requests.map((req) => (
        <ApprovalCard
          key={req.id}
          id={req.id}
          type="leave"
          requester={{
            name: req.employee_name ?? `Employee ${req.employee_id.slice(0, 6)}`,
            department: req.department,
          }}
          title={req.leave_type_name ?? 'Leave Request'}
          subtitle={`${req.start_date} → ${req.end_date}`}
          status="pending"
          requestDate={req.created_at}
          details={{
            Reason: req.reason ?? '—',
            'Start Date': req.start_date,
            'End Date': req.end_date,
          }}
          onApprove={onApprove}
          onReject={onReject}
          onViewDetails={onView}
        />
      ))}
    </div>
  );
}

// ── Leave Balance Widget ──────────────────────────────────────────────────────

interface LeaveBalance {
  leave_type_id: string;
  leave_type_name: string;
  opening_balance: number;
  accrued: number;
  taken: number;
  adjusted: number;
  available: number;
  financial_year: string;
}

function LeaveBalanceWidget() {
  const [balances, setBalances] = React.useState<LeaveBalance[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    callBLL('/v1/hros/leave/balance', 'GET')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setBalances(res.data as LeaveBalance[]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
        ))}
      </div>
    );
  }
  if (!balances.length) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        My Leave Balance — {balances[0]?.financial_year}
      </p>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {balances.map((b) => (
          <KpiCard
            key={b.leave_type_id}
            title={b.leave_type_name}
            value={b.available}
            icon={<Umbrella className="h-4 w-4" />}
            subtitle={`of ${b.opening_balance + b.accrued} days`}
            color={b.available <= 1 ? 'destructive' : b.available <= 3 ? 'warning' : 'success'}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function LeaveListPage() {
  const [activeTab, setActiveTab] = React.useState<'pending' | 'all'>('pending');
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [rejectDialogId, setRejectDialogId] = React.useState<string | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');

  // Fetch pending requests for manager view
  const { data: pendingData, isLoading: pendingLoading, refetch: refetchPending } = useList<LeaveRequest>({
    resource: 'leave_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'pending' }],
    pagination: { pageSize: 50 },
  });

  const pendingRequests = (pendingData?.data ?? []) as LeaveRequest[];

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Approve ───────────────────────────────────────────────────────────────
  async function handleApprove(id: string) {
    setActionLoading(id);
    try {
      const res = await callBLL(`/v1/hros/leave/requests/${id}/approve`);
      if (res.success) {
        setToast({ message: 'Leave approved successfully.', type: 'success' });
        refetchPending();
      } else {
        setToast({ message: res.error?.message ?? 'Approval failed.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error. Please try again.', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }

  // ── Reject flow ───────────────────────────────────────────────────────────
  function handleRejectClick(id: string) {
    setRejectDialogId(id);
    setRejectReason('');
  }

  async function handleRejectConfirm() {
    if (!rejectDialogId) return;
    setActionLoading(rejectDialogId);
    try {
      const res = await callBLL(`/v1/hros/leave/requests/${rejectDialogId}/reject`, 'POST', {
        reason: rejectReason || 'No reason provided',
      });
      if (res.success) {
        setToast({ message: 'Leave request rejected.', type: 'success' });
        refetchPending();
      } else {
        setToast({ message: res.error?.message ?? 'Rejection failed.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error. Please try again.', type: 'error' });
    } finally {
      setActionLoading(null);
      setRejectDialogId(null);
    }
  }

  function handleView(id: string) {
    window.location.href = `/leave/${id}`;
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Reject reason dialog */}
      {rejectDialogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Reject Leave Request</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Provide a reason for rejection (optional but recommended).
            </p>
            <textarea
              className="w-full border rounded-md p-2 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter rejection reason…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRejectDialogId(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={actionLoading === rejectDialogId}
                onClick={handleRejectConfirm}
              >
                {actionLoading === rejectDialogId ? 'Rejecting…' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Leave Requests</h1>
          {pendingRequests.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {pendingRequests.length} pending approval{pendingRequests.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Button onClick={() => (window.location.href = '/leave/create')}>
          + Apply Leave
        </Button>
      </div>

      {/* Leave balance KPI row */}
      <LeaveBalanceWidget />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending Approvals
            {pendingRequests.length > 0 && (
              <span className="ml-2 rounded-full bg-primary text-primary-foreground text-xs px-1.5 py-0.5">
                {pendingRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="all">All Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <PendingPanel
            requests={pendingRequests}
            isLoading={pendingLoading}
            onApprove={handleApprove}
            onReject={handleRejectClick}
            onView={handleView}
          />
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <ListPage
            collection="leave_requests"
            title=""
            basePath="/leave"
            excludeFields={['org_id', 'created_by', 'updated_at']}
            pageSize={25}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default LeaveListPage;
